import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, X, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#14b8a6"];

function Modal({ title, children, onClose }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}

function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

export default function CompaniesPage() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = () => axios.get(`${API}/companies`).then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`"${c.name}" şirketini silmek istediğinize emin misiniz?`)) return;
    try { await axios.delete(`${API}/companies/${c.id}`); load(); setErr(""); }
    catch (ex) { setErr(ex.response?.data?.detail || "Silinemedi"); }
  };

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">YÖNETİM</p>
        <h2>Şirketler</h2>
        <p className="muted">{rows.length} şirket · Tüm modüller buradaki şirketlere göre ayrışır. Silme işlemi yalnızca bağlı kayıt yoksa yapılabilir.</p>
      </div>
      <div className="toolbar-actions">
        <button className="primary-btn" data-testid="add-company-btn" onClick={() => { setEditing(null); setShowForm(true); }}>
          <Plus size={17} /> Yeni şirket
        </button>
      </div>
    </section>

    {err && <div className="auth-err" style={{ marginBottom: 14 }} data-testid="companies-err">{String(err)}</div>}

    <section className="panel full-panel">
      <div className="table-scroll"><table>
        <thead><tr><th>RENK</th><th>İSİM</th><th>KISA KOD</th><th>ID</th><th>OLUŞTURMA</th><th></th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="6" style={{ textAlign: "center", padding: 32 }} className="muted">
            Henüz şirket eklenmemiş. Üstteki "Yeni şirket" butonu ile başlayın.
          </td></tr>}
          {rows.map(c => <tr key={c.id} data-testid={`company-row-${c.id}`}>
            <td><span style={{ display: "inline-block", width: 24, height: 24, borderRadius: 6, background: c.color || "#3b82f6" }} /></td>
            <td><strong>{c.name}</strong></td>
            <td><span className="company-pill" style={{ background: (c.color || "#3b82f6") + "22", color: c.color || "#3b82f6" }}>{c.short || "—"}</span></td>
            <td className="muted" style={{ fontSize: 12 }}>{c.id}</td>
            <td>{(c.created_at || "").slice(0, 10)}</td>
            <td style={{ display: "flex", gap: 6 }}>
              <button className="outline-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`edit-company-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }}>Düzenle</button>
              <button className="icon-btn-sm" data-testid={`delete-company-${c.id}`} onClick={() => remove(c)} title="Sil"><Trash2 size={13} /></button>
            </td>
          </tr>)}
        </tbody>
      </table></div>
    </section>

    {showForm && <CompanyForm initial={editing} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
  </div>;
}

function CompanyForm({ initial, onClose, onDone }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    short: initial?.short || "",
    color: initial?.color || PRESET_COLORS[0],
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (initial) await axios.patch(`${API}/companies/${initial.id}`, form);
      else await axios.post(`${API}/companies`, form);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title={initial ? "Şirketi düzenle" : "Yeni şirket"}>
    <form onSubmit={submit} className="form">
      <Field label="Şirket adı">
        <input data-testid="company-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="örn. Atlas Holding" required autoFocus />
      </Field>
      <Field label="Kısa kod (opsiyonel · 2-4 harf)">
        <input data-testid="company-short" value={form.short} onChange={e => setForm({ ...form, short: e.target.value.toUpperCase() })} maxLength={4} placeholder="AH" />
      </Field>
      <Field label="Renk">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }} data-testid="company-color-grid">
          {PRESET_COLORS.map(c => (
            <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
              data-testid={`company-color-${c.replace("#", "")}`}
              style={{
                width: 32, height: 32, borderRadius: 8, border: form.color === c ? "3px solid var(--text)" : "2px solid transparent",
                background: c, cursor: "pointer", padding: 0,
              }} />
          ))}
          <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
            style={{ width: 32, height: 32, border: 0, borderRadius: 8, cursor: "pointer", background: "transparent" }} />
        </div>
      </Field>
      {err && <div className="auth-err" data-testid="company-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button className="primary-btn" disabled={busy} data-testid="company-submit">{busy ? "..." : (initial ? "Güncelle" : "Oluştur")}</button>
      </div>
    </form>
  </Modal>;
}
